/**
 * Las notas de filtro del Nodo de Datos (`filterNotes`, contrato «Filtros que se respetan» §4,
 * 2026-09-28): qué se le dice al usuario cuando un filtro NO se puede aplicar a una métrica.
 *
 * Puro (sin React): lo usan Informes (cada panel) y el Explorador (celda, Total y encabezado), con
 * el mismo texto para el mismo caso. El Backend reenvía las notas tal cual, como `filter_notes`:
 *
 *   {notApplicable:   [{metric, reason, filterField, sources, ingredient?}],
 *    partialSources:  [{metric, filterField, includedSources, excludedSources}],
 *    unattributedCost:[{metric, filterField, share}]}
 *
 * - Una métrica en `notApplicable` viaja en `null` en todas las filas. Se pinta «—» con el motivo,
 *   NUNCA vacía ni 0: un `null` que se muestra como dato ausente miente (dice «no hubo» cuando lo
 *   cierto es «no se puede saber con ese filtro»).
 * - **Sin notas, nada cambia**: todas las funciones devuelven `false` / `null` / `[]` y los
 *   componentes dibujan exactamente lo de siempre. El deploy del Frontend va ANTES que el del nodo.
 * - `ingredient` no está en el contrato: es una extensión opcional para que una fórmula (ROAS, CPA…)
 *   diga cuál de sus ingredientes es el que no aplica. Sin él, la fórmula se explica por su grupo.
 *
 * Los textos viven en `filter_notes.*` de `es.json` / `en.json` (`copy.md` de la tanda).
 */

import {CHANNEL_INFO_BY_CHANNEL} from '../components/channels/channels';

export const REASON_NOT_ATTRIBUTABLE = 'FILTER_NOT_ATTRIBUTABLE';
export const REASON_FIELD_NOT_IN_SOURCE = 'FILTER_FIELD_NOT_IN_SOURCE';

/** Por debajo de esta fracción del costo, la nota de «sin atribuir» no se muestra (contrato §4). */
export const UNATTRIBUTED_MIN_SHARE = 0.05;

/** Las dimensiones GA4 de NIVEL CAMPAÑA (contrato §1.2): las únicas que se atribuyen a la pauta. */
export const GA4_CAMPAIGN_LEVEL_FIELDS = Object.freeze([
    'GA4__Fuente', 'GA4__Medio', 'GA4__Fuente_medio', 'GA4__Campaña_sesión_dim', 'GA4__Agrupación_canal_sesión',
]);

/** Los códigos de error tipados de esta tanda (contrato §5) que el Frontend sabe explicar. */
export const FILTER_ERROR_CODES = Object.freeze({
    DIMENSION_FILTER_NOT_SUPPORTED: {withField: true},
    GA4_RESULT_TRUNCATED: {withField: false},
    UPSTREAM_QUOTA_EXCEEDED: {withField: false},
    INVALID_FIELD: {withField: true},
});

// Un nombre de campo puede traer «/» («GA4 - Fuente/Medio»): sin esto i18next lo escapa a entidad
// HTML y en pantalla se lee `Fuente&#x2F;Medio` (gotcha del CLAUDE.md del repo).
const NO_ESCAPE = {interpolation: {escapeValue: false}};

// `UNKNOWN`: el campo no está en el catálogo y su id no dice de qué es (una foto sin catálogo, un
// test). Se trata como pauta, que es lo que es casi todo lo que no lleva prefijo (Costo, Clicks,
// ROAS…). `OTHER` es distinto: está en el catálogo y NO es pauta (GSC, Klaviyo, personalizadas).
export const KIND = Object.freeze({GA4: 'GA4', STORE: 'STORE', PAID: 'PAID', OTHER: 'OTHER', UNKNOWN: 'UNKNOWN'});

// Grupos del catálogo (`looker_cc/fields_data.py`, `FIELDS_GROUP_*`) que son pauta. `MERCADO_ADS` es el
// de Product Ads (tanda «Mercado Ads como canal», 20/10): sin él sus campos salían como «otra fuente».
const PAID_GROUPS = ['PERFORMANCE', 'META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'MERCADO_ADS', 'ACCOUNT', 'ACCOUNT_CAMPAIGN', 'ACCOUNT_ADGROUP', 'ACCOUNT_AD'];
// Los grupos de pauta DENTRO de la campaña (`copy.md` §4): por plataforma, y los transversales
// (Grupo y familia Anuncio), que no tienen una plataforma que nombrar.
const PAID_IN_CAMPAIGN_PLATFORM_BY_GROUP = {META: 'FACEBOOK', GOOGLE: 'GOOGLE', TIKTOK: 'TIKTOK', LINKEDIN: 'LINKEDIN',
    MERCADO_ADS: 'MERCADO_ADS'};
const PAID_IN_CAMPAIGN_CROSS_GROUPS = ['ACCOUNT_ADGROUP', 'ACCOUNT_AD'];
// Sin catálogo a mano (una foto del portal, un test), el prefijo del id alcanza para clasificar.
const PAID_IN_CAMPAIGN_PLATFORM_BY_PREFIX = [
    [/^Meta_/, 'FACEBOOK'], [/^Google_/, 'GOOGLE'], [/^Tiktok_/i, 'TIKTOK'], [/^Linkedin_/i, 'LINKEDIN'],
];
const ECOMMERCE_CHANNELS = ['SHOPIFY', 'TIENDA_NUBE', 'WOOCOMMERCE', 'VTEX'];

// Cómo se nombra cada plataforma en las notas («Meta», «Google Ads»: `copy.md` §2). Sólo los canales
// cuyo nombre de las notas difiere del de `CHANNEL_INFO_BY_CHANNEL` (que dice «Facebook Ads»); el
// resto cae en ese catálogo, así que un canal nuevo sale con su nombre y no con su código.
const PLATFORM_NAMES = {
    FACEBOOK: 'Meta',
    TIKTOK: 'TikTok Ads',
    TIENDA_NUBE: 'Tiendanube',
    SHEET: 'CSV',
};

/** El nombre visible de una plataforma (canal del backend); el código tal cual si no se conoce. */
export function platformName(channel) {
    if (PLATFORM_NAMES[channel]) return PLATFORM_NAMES[channel];
    const info = CHANNEL_INFO_BY_CHANNEL[channel];
    return (info && info.Spanish) || channel || '';
}

// ------------------------------------------------------------------ normalización

function asList(value) {
    return Array.isArray(value) ? value : [];
}

function pick(obj, camel, snake) {
    if (!obj) return undefined;
    return obj[camel] !== undefined ? obj[camel] : obj[snake];
}

function normalizeNotApplicable(entry) {
    return {
        metric: entry.metric,
        reason: entry.reason || REASON_NOT_ATTRIBUTABLE,
        filterField: pick(entry, 'filterField', 'filter_field') || null,
        sources: asList(entry.sources),
        ingredient: entry.ingredient || null,
    };
}

function normalizePartial(entry) {
    return {
        metric: entry.metric,
        filterField: pick(entry, 'filterField', 'filter_field') || null,
        includedSources: asList(pick(entry, 'includedSources', 'included_sources')),
        excludedSources: asList(pick(entry, 'excludedSources', 'excluded_sources')),
    };
}

function normalizeUnattributed(entry) {
    return {
        metric: entry.metric,
        filterField: pick(entry, 'filterField', 'filter_field') || null,
        share: typeof entry.share === 'number' ? entry.share : Number(entry.share) || 0,
    };
}

/**
 * Las notas en su forma canónica, o `null` si no hay ninguna. Tolera las claves en camelCase (el
 * contrato) y en snake_case, y cualquier lista ausente.
 */
export function normalizeFilterNotes(raw) {
    if (!raw || typeof raw !== 'object') return null;
    // Ya normalizado (la tabla lo hace una vez por render y después pregunta por celda): tal cual.
    if (raw[NORMALIZED]) return raw;
    const notes = {
        notApplicable: asList(pick(raw, 'notApplicable', 'not_applicable')).filter((e) => e && e.metric).map(normalizeNotApplicable),
        partialSources: asList(pick(raw, 'partialSources', 'partial_sources')).filter((e) => e && e.metric).map(normalizePartial),
        unattributedCost: asList(pick(raw, 'unattributedCost', 'unattributed_cost')).filter((e) => e && e.metric).map(normalizeUnattributed),
    };
    if (!notes.notApplicable.length && !notes.partialSources.length && !notes.unattributedCost.length) return null;
    Object.defineProperty(notes, NORMALIZED, {value: true, enumerable: false});
    return notes;
}

const NORMALIZED = Symbol('filterNotes.normalized');

/** Las notas de la TABLA (sus filas y columnas): `filter_notes` en el Explorador, `filterNotes` en Informes. */
export function tableFilterNotesOf(results) {
    if (!results) return null;
    return results.filter_notes || results.filterNotes || null;
}

/**
 * Las notas que valen para los TOTALES (la fila Total, «Sin desglose», la tarjeta): salen de la
 * consulta de totales, que el nodo planifica distinto que la tabla (card x4TLNeOw). El Backend las
 * manda aparte —`summary_filter_notes` / `summaryFilterNotes`— sólo cuando dicen otra cosa que las de
 * la tabla, incluso nada (`{}`). Por eso decide la PRESENCIA de la clave: si está, manda ella aunque
 * venga vacía; si no está, valen las de la tabla.
 */
export function summaryFilterNotesOf(results) {
    if (!results) return null;
    for (const key of ['summary_filter_notes', 'summaryFilterNotes']) {
        if (Object.prototype.hasOwnProperty.call(results, key)) return results[key] || null;
    }
    return tableFilterNotesOf(results);
}

/** Las métricas que no aplican, como `Set`, para preguntar N veces (una celda por fila) sin recorrer. */
export function notApplicableMetricsOf(notes) {
    const normalized = normalizeFilterNotes(notes);
    return new Set(normalized ? normalized.notApplicable.map((e) => e.metric) : []);
}

// ------------------------------------------------------------------ catálogo

function fieldOf(fieldId, fieldsData) {
    const byId = (fieldsData && fieldsData.FIELD_DATA_BY_ID) || {};
    return (fieldId && byId[fieldId]) || null;
}

/** El nombre visible de un campo (el del catálogo, «GA4 - País»); el id si el catálogo no lo conoce. */
export function fieldLabelOf(fieldId, fieldsData) {
    const field = fieldOf(fieldId, fieldsData);
    return (field && field.name) || fieldId || '';
}

/**
 * De qué es un campo: GA4, tienda, pauta, otra fuente (`OTHER`) o desconocido (`UNKNOWN`). Lo usa
 * también la regla de «no aplica» por fila de la tabla (`data_explorer/channelApplicability.js`)
 * cuando el catálogo no trae `FIELD_APPLIES_TO_CHANNELS`.
 */
export function fieldKind(fieldId, fieldsData) {
    const field = fieldOf(fieldId, fieldsData);
    const group = (field && field.group) || null;
    const id = fieldId || '';
    if (group === 'GA4' || group === 'GA4_CUSTOM' || /^GA4C?__/.test(id)) return KIND.GA4;
    if (group === 'ECOMMERCE' || /^Ecommerce_/.test(id)) return KIND.STORE;
    if (PAID_GROUPS.includes(group) || PAID_IN_CAMPAIGN_PLATFORM_BY_PREFIX.some(([re]) => re.test(id))) return KIND.PAID;
    return field ? KIND.OTHER : KIND.UNKNOWN;
}

function isPaid(kind) {
    return kind === KIND.PAID || kind === KIND.UNKNOWN;
}

function sourcesAreStore(sources) {
    return sources.length > 0 && sources.every((s) => ECOMMERCE_CHANNELS.includes(s));
}

// ------------------------------------------------------------------ «no aplica»

/** La entrada de `notApplicable` de una métrica, o `null`. */
export function notApplicableEntry(notes, metric) {
    const normalized = normalizeFilterNotes(notes);
    if (!normalized || !metric) return null;
    return normalized.notApplicable.find((e) => e.metric === metric) || null;
}

/** ¿La métrica no se puede calcular con los filtros pedidos? Sin notas, nunca. */
export function isNotApplicable(notes, metric) {
    return notApplicableEntry(notes, metric) !== null;
}

/**
 * El motivo, elegido según `copy.md` §1:
 *  - una fórmula con un ingrediente que no aplica → `na_formula`;
 *  - `FILTER_FIELD_NOT_IN_SOURCE` sobre la tienda → `na_store`;
 *  - un filtro GA4 sobre una métrica de PAUTA → `na_ga4_in_campaign`;
 *  - un filtro de PAUTA sobre una métrica GA4 → `na_paid_on_ga4`;
 *  - el resto (otra plataforma paid, GSC, Klaviyo, métricas personalizadas, una fuente que no
 *    tiene el campo) → `na_other_platform`.
 * Las dos del medio exigen que el OTRO lado sea pauta (review C-fe-06): una métrica de Search
 * Console con un filtro GA4 no «divide el tráfico dentro de cada campaña», es de otra plataforma.
 */
export function notApplicableMessage(notes, metric, t, fieldsData = null) {
    const entry = notApplicableEntry(notes, metric);
    if (!entry) return null;
    const params = {
        metric: fieldLabelOf(metric, fieldsData),
        field: fieldLabelOf(entry.filterField, fieldsData),
        ...NO_ESCAPE,
    };
    if (entry.ingredient) {
        return t('filter_notes.na_formula', {...params, ingredient: fieldLabelOf(entry.ingredient, fieldsData)});
    }
    const metricKind = fieldKind(metric, fieldsData);
    const filterKind = fieldKind(entry.filterField, fieldsData);
    if (entry.reason === REASON_FIELD_NOT_IN_SOURCE) {
        if (metricKind === KIND.STORE || sourcesAreStore(entry.sources)) return t('filter_notes.na_store', params);
        return t('filter_notes.na_other_platform', params);
    }
    if (filterKind === KIND.GA4 && isPaid(metricKind)) return t('filter_notes.na_ga4_in_campaign', params);
    if (metricKind === KIND.GA4 && isPaid(filterKind)) return t('filter_notes.na_paid_on_ga4', params);
    return t('filter_notes.na_other_platform', params);
}

// ------------------------------------------------------------------ notas del panel

/** «Meta, Google Ads y TikTok Ads». */
export function joinPlatformNames(channels, t) {
    const names = asList(channels).map(platformName).filter(Boolean);
    if (names.length <= 1) return names.join('');
    const and = t('filters.and', 'y');
    return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;
}

/** El texto de una entrada de `partialSources`. */
export function partialNote(entry, t, fieldsData = null) {
    if (!entry || !entry.metric) return null;
    const e = normalizePartial(entry);
    // `count` elige el plural del verbo («no tiene» / «no tienen»): con dos plataformas excluidas la
    // frase iba en singular (QA-VIS-filtrosfe-24). Claves `filter_notes.partial_one` / `_other`.
    return t('filter_notes.partial', {
        count: Math.max(1, e.excludedSources.length),
        included: joinPlatformNames(e.includedSources, t),
        excluded: joinPlatformNames(e.excludedSources, t),
        field: fieldLabelOf(e.filterField, fieldsData),
        ...NO_ESCAPE,
    });
}

/** Quién lee la nota: la agencia (default) o el cliente en su portal, que no configura UTM. */
export const AUDIENCE = Object.freeze({AGENCY: 'agency', PORTAL: 'portal'});

/**
 * El texto de una entrada de `unattributedCost`, o `null` si la parte no llega al 5 %. En el portal
 * va sin la indicación de revisar las UTM: es trabajo de la agencia, no del cliente (review C-fe-09).
 */
export function unattributedNote(entry, t, audience = AUDIENCE.AGENCY) {
    if (!entry || !entry.metric) return null;
    const e = normalizeUnattributed(entry);
    if (!(e.share > UNATTRIBUTED_MIN_SHARE)) return null;
    const key = audience === AUDIENCE.PORTAL ? 'filter_notes.unattributed_portal' : 'filter_notes.unattributed';
    return t(key, {pct: Math.round(e.share * 100), ...NO_ESCAPE});
}

/**
 * Las notas (parciales y sin atribuir) que corresponden a un conjunto de métricas, sin repetir y en
 * el orden en que vienen. Es lo que va debajo del valor de una tarjeta o en el ⓘ de una columna.
 */
export function notesForMetrics(notes, metrics, t, fieldsData = null, audience = AUDIENCE.AGENCY) {
    const normalized = normalizeFilterNotes(notes);
    if (!normalized) return [];
    const wanted = asList(metrics);
    const texts = [];
    const add = (text) => { if (text && !texts.includes(text)) texts.push(text); };
    normalized.partialSources.filter((e) => wanted.includes(e.metric)).forEach((e) => add(partialNote(e, t, fieldsData)));
    normalized.unattributedCost.filter((e) => wanted.includes(e.metric)).forEach((e) => add(unattributedNote(e, t, audience)));
    return texts;
}

// ------------------------------------------------------------------ ayuda del filtro

/**
 * La ayuda secundaria al elegir un campo para filtrar (`copy.md` §4), o `null` para los campos
 * que no la llevan (Canal, Cuenta, línea, Campaña, métricas, ecommerce…).
 *
 * Sólo cuando el catálogo dice que el plan de filtros está APLICADO
 * (`fieldsData.FILTER_PLAN_ENABLED === true`, dentro de `fields_data` de `form-data`). El Backend lo
 * publica GLOBAL, no por agencia (`settings.FILTER_PLAN_ENABLED`, card x4TLNeOw): va en `true` sólo
 * junto con `FILTER_PLAN_MODE=on` y `FILTER_PLAN_AGENCIES=*` del nodo. Si el plan se prendiera para
 * un subconjunto de agencias, este flag no sirve: habría que publicarlo por agencia. Sin esa señal la
 * ayuda promete un comportamiento que la agencia todavía no tiene («filtra también el costo») y rompe
 * la puerta «sin notas, nada cambia» (review C-fe-01).
 */
export function filterFieldHint(fieldId, fieldsData, t) {
    if (!fieldId) return null;
    if (!fieldsData || fieldsData.FILTER_PLAN_ENABLED !== true) return null;
    const field = fieldOf(fieldId, fieldsData);
    const segments = (fieldsData && fieldsData.FIELD_SEGMENTS) || {};
    if (field && segments.METRICS && field.segment === segments.METRICS) return null;
    if (GA4_CAMPAIGN_LEVEL_FIELDS.includes(fieldId)) return t('filter_notes.hint_ga4_campaign_level');
    if (fieldKind(fieldId, fieldsData) === KIND.GA4) return t('filter_notes.hint_ga4_in_campaign');
    const group = (field && field.group) || null;
    let platform = PAID_IN_CAMPAIGN_PLATFORM_BY_GROUP[group] || null;
    if (!platform && !group) {
        const byPrefix = PAID_IN_CAMPAIGN_PLATFORM_BY_PREFIX.find(([re]) => re.test(fieldId));
        platform = byPrefix ? byPrefix[1] : null;
    }
    if (platform) return t('filter_notes.hint_paid_in_campaign', {platform: platformName(platform), ...NO_ESCAPE});
    if (PAID_IN_CAMPAIGN_CROSS_GROUPS.includes(group)) return t('filter_notes.hint_paid_in_campaign_all');
    return null;
}

// ------------------------------------------------------------------ errores tipados

/**
 * El texto de un error tipado de esta tanda (`copy.md` §3), o `null` si el código no es de acá.
 * `params.field` es el id del campo que el backend nombra, cuando lo manda; sin él, la variante
 * que no lo nombra.
 */
export function filterErrorText(code, params, t, fieldsData = null) {
    const spec = code && FILTER_ERROR_CODES[code];
    if (!spec) return null;
    if (!spec.withField) return t(`filter_notes.error.${code}`);
    const field = params && params.field ? fieldLabelOf(params.field, fieldsData) : null;
    if (!field) return t(`filter_notes.error.${code}_unknown_field`);
    return t(`filter_notes.error.${code}`, {field, ...NO_ESCAPE});
}
